// Truncate a long string to maxLength and add "..." if needed
export const truncateDescription = (description, maxLength = 297) => {
  if (!description) return '';
  const strippedText = stripHtmlTags(description);
  return strippedText.length > maxLength
    ? `${strippedText.slice(0, maxLength)}...`
    : strippedText;
};

export const truncateTitle = (title, maxLength = 180) => {
  if (!title) return '';
  return title.length > maxLength
    ? `${title.substring(0, maxLength - 3)}...`
    : title;
};

// Strip all HTML tags from a string
export const stripHtmlTags = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Format ISO date string into MM-DD-YYYY
export const formatDate = (inputDate) => {
  if (!inputDate) return '';
  const date = new Date(inputDate);
  return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
};

// Enhanced country details function for dual API system
export const getCountryDetails = (countrySchema) => {
  const countryMap = {
    'all': { name: 'All Countries', emoji: '🌐', link: '/', apiCode: 'ALL', legacyCode: '' },
    'us': { name: 'United States', emoji: '🇺🇸', link: '/us', apiCode: 'US', legacyCode: 'united_states_of_america' },
    'canada': { name: 'Canada', emoji: '🇨🇦', link: '/canada', apiCode: 'CA', legacyCode: 'canada' },
    'mexico': { name: 'Mexico', emoji: '🇲🇽', link: '/mexico', apiCode: 'MX', legacyCode: 'mexico' },
    'uk': { name: 'United Kingdom', emoji: '🇬🇧', link: '/uk', apiCode: 'UK', legacyCode: 'united_kingdom' },
    'united_kingdom': { name: 'United Kingdom', emoji: '🇬🇧', link: '/uk', apiCode: 'UK', legacyCode: 'united_kingdom' },
    'united_states_of_america': { name: 'United States', emoji: '🇺🇸', link: '/us', apiCode: 'US', legacyCode: 'united_states_of_america' },
    '': { name: 'All Countries', emoji: '🌐', link: '/', apiCode: 'ALL', legacyCode: '' }
  };
  return countryMap[countrySchema] || { name: 'Unknown', emoji: '❓', link: '/', apiCode: 'ALL', legacyCode: '' };
};

// Open a URL in a new tab
export const openLinkInBrowser = (url) => {
  if (!url) return;
  window.open(url, '_blank');
};

// API key - use environment variable or fallback to hardcoded value
export const getApiKey = () => {
  return process.env.REACT_APP_API_KEY;
};

// Legacy API search (kept for backward compatibility, but SearchPage now handles this)
export const runSearch = async (limit, query, country = '') => {
  if (!query || !query.trim()) {
    throw new Error('Search query is empty');
  }

  const encodedQuery = encodeURIComponent(query);
  const baseUrl = 'https://api.govglance.org/search/';
  const url = country
    ? `${baseUrl}?limit=${limit}&skip=0&schema=${country}&search_term=${encodedQuery}`
    : `${baseUrl}?limit=${limit}&skip=0&search_term=${encodedQuery}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getApiKey()}`,
    },
  });

  if (response.status === 404) throw new Error('No results found');
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  return await response.json();
};

// New main search API function
export const runMainSearch = async (query, country = '') => {
  if (!query || !query.trim()) {
    throw new Error('Search query is empty');
  }

  const countryDetails = getCountryDetails(country);
  
  let url = `https://search.govglance.org/api/public-search?query=${encodeURIComponent(query)}`;
  if (countryDetails.apiCode && countryDetails.apiCode !== 'ALL') {
    url += `&country=${countryDetails.apiCode}`;
  }
  url += '&format=json';

  const response = await fetch(url, { 
    method: 'GET', 
    headers: { 'Content-Type': 'application/json' } 
  });

  if (response.status === 404) throw new Error('No results found');
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  return await response.json();
};

// Legacy API search function
export const runLegacySearch = async (query, country) => {
  if (!query || !query.trim()) {
    throw new Error('Search query is empty');
  }

  const countryDetails = getCountryDetails(country);
  
  if (!countryDetails.legacyCode || countryDetails.legacyCode === '') {
    throw new Error('No legacy API support for this country');
  }

  const url = `https://api.govglance.org/search/?limit=20&skip=0&schema=${countryDetails.legacyCode}&search_term=${encodeURIComponent(query)}`;

  const response = await fetch(url, { 
    method: 'GET', 
    headers: { 
      'Content-Type': 'application/json',
      "Authorization": `Bearer ${getApiKey()}`
    } 
  });

  if (response.status === 404) {
    return [];
  }
  
  if (response.status === 401) {
    throw new Error('Authentication failed for legacy API');
  }
  
  if (!response.ok) {
    throw new Error(`Legacy API error! status: ${response.status}`);
  }

  return await response.json();
};

// Check if country should use legacy API
export const shouldUseLegacyAPI = (countryCode) => {
  const countryDetails = getCountryDetails(countryCode);
  const legacyCountries = [
    'us', 'canada', 'mexico', 'uk', 'united_kingdom', 'united_states_of_america'
  ];
  return legacyCountries.includes(countryCode) && countryDetails.legacyCode !== '';
};